import React, { useEffect, useRef, useState } from 'react';
import * as tus from 'tus-js-client';
import {
  authoriseUpload,
  StreamUnavailableError,
} from '../../services/streamUploadService';
import { useStreamEncodingStatus } from '../../hooks/useStreamEncodingStatus';
import { forget, remember, rememberedAuthorisation } from './streamUploadStorage';

/**
 * Uploads a film straight from this browser to Bunny Stream.
 *
 * ## What it replaces
 *
 * Getting a film onto Stream used to take four manual steps: upload in the Bunny
 * dashboard, wait, copy the video id, paste it into StrymHub. An id was pasted against
 * the wrong one of two similarly named intros once already. Here the id is attached
 * automatically, to the item being created.
 *
 * ## Resuming, properly
 *
 * A large film on a home connection takes hours, and connections drop. TUS resumes from
 * the last byte Bunny confirmed - but only into the SAME Bunny video. Each authorisation
 * creates a new video, so naively re-authorising on retry would start an empty second
 * video and leave the first half-uploaded forever.
 *
 * So the authorisation is remembered per file (name, size, last-modified) until it
 * expires. Choosing the same file again - after a crash, a closed tab, a dropped
 * connection - picks up the same video and continues from where it stopped.
 */

interface Props {
  /** The content title. Required first, because it names the video in the library. */
  title: string;
  disabled?: boolean;
  /** Called once the bytes are all at Bunny. Encoding continues after this. */
  onUploaded: (videoId: string, fileName: string) => void;
  /** Called with true once every resolution has finished encoding. */
  onEncodingFinished?: (finished: boolean) => void;
  /** Called when the server has no Stream configuration, so the form can fall back. */
  onUnavailable: () => void;
}

type Phase = 'idle' | 'authorising' | 'uploading' | 'uploaded' | 'error';

function formatBytes(bytes: number): string {
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

const StreamVideoUpload: React.FC<Props> = ({
  title,
  disabled,
  onUploaded,
  onEncodingFinished,
  onUnavailable,
}) => {
  const [phase, setPhase] = useState<Phase>('idle');
  const [progress, setProgress] = useState(0);
  const [bytes, setBytes] = useState<{ sent: number; total: number }>({ sent: 0, total: 0 });
  const [message, setMessage] = useState<string | null>(null);
  const [videoId, setVideoId] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string>('');
  const uploadRef = useRef<tus.Upload | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const { status: encoding, error: encodingError } = useStreamEncodingStatus(
    videoId,
    phase === 'uploaded',
  );

  useEffect(() => {
    onEncodingFinished?.(!!encoding?.finished);
  }, [encoding?.finished, onEncodingFinished]);

  // Closing the tab mid-upload throws away the transfer. For a large film that can be
  // hours, so ask first. (It resumes if they come back and choose the same file, but
  // they should not have to know that.)
  useEffect(() => {
    if (phase !== 'uploading') return;
    const warn = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [phase]);

  // Stop the transfer if the form closes. It stays resumable.
  useEffect(() => () => { uploadRef.current?.abort(); }, []);

  const start = async (file: File) => {
    if (!title.trim()) {
      setPhase('error');
      setMessage('Enter the title first - it is how this video is identified in the Bunny library.');
      return;
    }

    setFileName(file.name);
    setMessage(null);
    setProgress(0);

    let auth = rememberedAuthorisation(file);
    // Whether this continues an earlier upload into the SAME Bunny video. Only then is
    // it safe to resume; a fresh authorisation is a different, empty video.
    const continuing = !!auth;
    if (!auth) {
      setPhase('authorising');
      try {
        auth = await authoriseUpload(title.trim());
        remember(file, auth);
      } catch (e) {
        if (e instanceof StreamUnavailableError) {
          onUnavailable();
          return;
        }
        setPhase('error');
        setMessage(e instanceof Error ? e.message : 'Could not start the upload.');
        return;
      }
    }

    const authorisation = auth;
    const upload = new tus.Upload(file, {
      endpoint: authorisation.endpoint,
      retryDelays: [0, 3000, 5000, 10000, 20000, 60000, 60000],
      // A finished upload has nothing left to resume, and a lingering record would be
      // offered as a "previous upload" of this file the next time it is chosen.
      removeFingerprintOnSuccess: true,
      headers: {
        AuthorizationSignature: authorisation.signature,
        AuthorizationExpire: String(authorisation.expiresAt),
        VideoId: authorisation.videoId,
        LibraryId: authorisation.libraryId,
      },
      metadata: {
        filetype: file.type || 'video/mp4',
        title: title.trim(),
      },
      onError: (err) => {
        setPhase('error');
        setMessage(
          `Upload interrupted: ${err.message}. Choose the same file again to continue from where it stopped.`,
        );
      },
      onProgress: (sent, total) => {
        setBytes({ sent, total });
        setProgress(total ? Math.floor((sent / total) * 100) : 0);
      },
      onSuccess: () => {
        forget(file);
        setVideoId(authorisation.videoId);
        setPhase('uploaded');
        onUploaded(authorisation.videoId, file.name);
      },
    });

    uploadRef.current = upload;
    setPhase('uploading');

    const previous = await upload.findPreviousUploads();
    if (continuing && previous.length) {
      // Most recent first. The newest record is the one tied to the authorisation we
      // just reused.
      const latest = [...previous].sort(
        (a, b) => new Date(b.creationTime).getTime() - new Date(a.creationTime).getTime(),
      )[0];
      upload.resumeFromPreviousUpload(latest);
      setMessage('Resuming a previous upload of this file.');
    } else if (previous.length) {
      // A NEW Bunny video, but TUS still remembers an old partial upload of this file -
      // one whose authorisation expired. Resuming it would send bytes to the old video
      // under the new video's credentials, and Bunny would refuse or misfile them.
      // Discard those records and start clean.
      const storage = upload.options.urlStorage;
      await Promise.all(
        previous.map((p) => (p.urlStorageKey && storage ? storage.removeUpload(p.urlStorageKey) : Promise.resolve())),
      );
    }
    upload.start();
  };

  const cancel = () => {
    uploadRef.current?.abort();
    setPhase('idle');
    setMessage('Upload paused. Choose the same file again to continue.');
  };

  if (phase === 'uploaded') {
    const finished = !!encoding?.finished;
    const failed = !!encoding?.failed;
    return (
      <div className={`rounded-lg border p-4 ${failed ? 'bg-red-50 border-red-200' : finished ? 'bg-green-50 border-green-200' : 'bg-blue-50 border-blue-200'}`}>
        <p className={`text-sm font-medium ${failed ? 'text-red-800' : finished ? 'text-green-800' : 'text-blue-800'}`}>
          {failed ? '✗ ' : finished ? '✅ ' : '⏳ '}{fileName}
        </p>
        <p className={`text-xs mt-1 ${failed ? 'text-red-700' : finished ? 'text-green-700' : 'text-blue-700'}`}>
          {failed && 'Bunny could not encode this file. Try re-exporting it as H.264 or H.265 MP4 and upload again.'}
          {finished && `Ready to stream${encoding?.availableResolutions ? ` in ${encoding.availableResolutions}` : ''}.`}
          {!failed && !finished && (
            <>
              Uploaded. Bunny is encoding it
              {encoding?.encodeProgress != null ? ` (${encoding.encodeProgress}%)` : ''}
              {encoding?.availableResolutions ? ` - available so far: ${encoding.availableResolutions}` : ''}.
              {' '}You can save as a Draft now and publish once encoding finishes. Long films can take hours.
            </>
          )}
        </p>
        {encodingError && !finished && (
          <p className="text-xs mt-1 text-gray-600">Status check failed ({encodingError}) - retrying.</p>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <input
        ref={inputRef}
        type="file"
        accept="video/*"
        disabled={disabled || phase === 'uploading' || phase === 'authorising'}
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) void start(file);
          // Allow choosing the same file again to resume after an interruption.
          e.target.value = '';
        }}
        className="block w-full text-sm text-gray-700 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-orange-50 file:text-orange-700 hover:file:bg-orange-100 disabled:opacity-50"
      />

      {phase === 'authorising' && <p className="text-sm text-gray-600">Preparing upload…</p>}

      {phase === 'uploading' && (
        <div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div className="bg-orange-500 h-2 rounded-full transition-all" style={{ width: `${progress}%` }} />
          </div>
          <div className="flex justify-between items-center mt-1">
            <p className="text-xs text-gray-600">
              {progress}% · {formatBytes(bytes.sent)} of {formatBytes(bytes.total)} · uploading directly to Bunny
            </p>
            <button type="button" onClick={cancel} className="text-xs text-gray-600 underline hover:text-gray-900">
              Pause
            </button>
          </div>
        </div>
      )}

      {message && (
        <p className={`text-sm ${phase === 'error' ? 'text-red-600' : 'text-gray-600'}`}>{message}</p>
      )}

      {phase === 'idle' && !message && (
        <p className="text-xs text-gray-500">
          Goes straight from this browser to Bunny Stream and resumes if the connection drops. Export from your editor as H.264 or H.265 MP4.
        </p>
      )}
    </div>
  );
};

export default StreamVideoUpload;
